import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiTruck, HiCheckCircle, HiClock, HiExclamationCircle } from 'react-icons/hi2';
import { getDeliveries } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';

const statusBadge = s => {
  const map = { scheduled: 'badge-blue', in_transit: 'badge-purple', delivered: 'badge-green', failed: 'badge-red', rescheduled: 'badge-yellow' };
  return <span className={`badge ${map[s] || 'badge-gray'}`}>{s.replace('_', ' ')}</span>;
};

export default function StaffDashboard() {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    getDeliveries({ date: today, limit: 50 })
      .then(r => setDeliveries(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [today]);

  const stats = {
    total: deliveries.length,
    delivered: deliveries.filter(d => d.status === 'delivered').length,
    pending: deliveries.filter(d => d.status === 'scheduled').length,
    in_transit: deliveries.filter(d => d.status === 'in_transit').length,
  };

  return (
    <div className="content-area">
      <div className="page-header">
        <div>
          <h1 className="page-title">Good morning, {user?.name}! 👋</h1>
          <p className="text-muted">Today's delivery summary — {format(new Date(), 'EEEE, dd MMM yyyy')}</p>
        </div>
        <Link to="/staff/deliveries" className="btn btn-primary">View All Deliveries</Link>
      </div>

      <div className="grid grid-4" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ background: '#dbeafe', padding: '0.6rem', borderRadius: '0.5rem' }}>
              <HiTruck style={{ color: '#1d4ed8', fontSize: '1.25rem' }} />
            </div>
            <div>
              <p className="stat-label">Today's Total</p>
              <p className="stat-value" style={{ fontSize: '1.5rem' }}>{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ background: '#dcfce7', padding: '0.6rem', borderRadius: '0.5rem' }}>
              <HiCheckCircle style={{ color: '#16a34a', fontSize: '1.25rem' }} />
            </div>
            <div>
              <p className="stat-label">Delivered</p>
              <p className="stat-value" style={{ fontSize: '1.5rem', color: '#16a34a' }}>{stats.delivered}</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ background: '#fef3c7', padding: '0.6rem', borderRadius: '0.5rem' }}>
              <HiClock style={{ color: '#b45309', fontSize: '1.25rem' }} />
            </div>
            <div>
              <p className="stat-label">Scheduled</p>
              <p className="stat-value" style={{ fontSize: '1.5rem', color: '#b45309' }}>{stats.pending}</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ background: '#ede9fe', padding: '0.6rem', borderRadius: '0.5rem' }}>
              <HiExclamationCircle style={{ color: '#7c3aed', fontSize: '1.25rem' }} />
            </div>
            <div>
              <p className="stat-label">In Transit</p>
              <p className="stat-value" style={{ fontSize: '1.5rem', color: '#7c3aed' }}>{stats.in_transit}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Today's Deliveries</h2>
        </div>
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : deliveries.length === 0 ? (
          <div className="card-body" style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
            <HiTruck style={{ fontSize: '3rem', marginBottom: '0.5rem', display: 'block', margin: '0 auto 0.5rem' }} />
            No deliveries scheduled for today.
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Order</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d, i) => (
                  <tr key={d.id}>
                    <td>{i + 1}</td>
                    <td><strong>{d.customer_name}</strong></td>
                    <td>{d.customer_phone}</td>
                    <td className="text-muted" style={{ maxWidth: 200 }}>{d.customer_address}</td>
                    <td><span className="badge badge-blue">{d.order_number}</span></td>
                    <td>{statusBadge(d.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
